import type { StoryDefaultPlatform } from 'types/story';

export type DesignModeSelection = Record<string, string>;

export type DesignModeValue<T> = {
	base: T;
	overrides?: Array<{
		when: DesignModeSelection;
		value: T;
	}>;
};

export type DesignMode = {
	id: string;
	name: string;
	colorScheme?: 'light' | 'dark';
};

export type DesignModeGroup = {
	id: string;
	name: string;
	defaultModeId: string;
	modes: DesignMode[];
};

export type DesignModeActivation =
	| { type: 'fixed'; modeId: string }
	| {
			type: 'media';
			feature: 'prefers-color-scheme';
			mapping: { light: string; dark: string };
	  };

export type StoryDesignModeSettings = {
	activation: Record<string, DesignModeActivation>;
};

export type DesignTokenType = 'color' | 'dimension' | 'fontFamily' | 'number';
export type DesignToken = { id: string; name: string; description?: string } & (
	| { type: 'color' | 'dimension' | 'fontFamily'; value: DesignModeValue<string> }
	| { type: 'number'; value: DesignModeValue<number> }
);
export type TokenCollection = { id: string; name: string; tokens: DesignToken[] };
export type DesignTokenSet = { schemaVersion: 1; collections: TokenCollection[] };

export type DesignAssetRole = 'logo' | 'compactLogo' | 'share' | 'favicon' | 'other';
export type DesignAsset = {
	id: string;
	name: string;
	role: DesignAssetRole;
	type: 'image';
	value: DesignModeValue<string>;
	description?: string;
};
export type DesignAssetSet = { schemaVersion: 1; assets: DesignAsset[] };

export const TEXT_PRESET_PROPERTIES = [
	'fontFamily',
	'fontSize',
	'lineHeight',
	'letterSpacing',
	'fontWeight',
	'color',
	'backgroundColor',
	'textTransform',
	'textDecoration',
	'marginTop',
	'marginRight',
	'marginBottom',
	'marginLeft',
	'paddingTop',
	'paddingRight',
	'paddingBottom',
	'paddingLeft',
] as const;
export type TextPresetProperty = (typeof TEXT_PRESET_PROPERTIES)[number];
export type TextPresetValue = string | number;
export type TextPresetPropertyValue = {
	base?: TextPresetValue;
	platforms?: Partial<Record<StoryDefaultPlatform, TextPresetValue>>;
};
export type TextPreset = {
	id: string;
	name: string;
	description?: string;
	properties: Partial<Record<TextPresetProperty, TextPresetPropertyValue>>;
};

export type DesignDefinition = {
	schemaVersion: 1;
	modeGroups: DesignModeGroup[];
	defaults: { root: { fontFamily: DesignModeValue<string> } };
	tokens: DesignTokenSet;
	assets: DesignAssetSet;
	textPresets?: TextPreset[];
};
